import { errorToString, isActionError, type ActionResponse } from '@/lib/errors'
import * as Sentry from '@sentry/nextjs'
import type { Route } from 'next'
import { UserSession } from './types'

export type TimeOpts =
    | { [key: number]: 'ms' }
    | { 1: 'second' }
    | { [key: number]: 'seconds' }
    | { 1: 'minute' }
    | { [key: number]: 'minutes' }

export function timeOptsToMS(opts: TimeOpts) {
    let ms = 0
    for (const [key, unit] of Object.entries(opts)) {
        const duration = parseInt(key, 10)
        if (unit === 'ms') {
            ms += duration
        } else if (unit.startsWith('second')) {
            ms += duration * 1000
        } else if (unit.startsWith('minute')) {
            ms += duration * 60 * 1000
        }
    }
    return ms
}

export async function sleep(opts: TimeOpts): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        try {
            setTimeout(resolve, timeOptsToMS(opts))
        } catch (error) {
            Sentry.captureException(error)
            reject(error)
        }
    })
}

export function sanitizeFileName(fileName: string) {
    return (
        fileName
            .substring(0, 512)
            .replace(/^\/+/, '')
            .replace(/\.\./g, '')
            // strips non-ascii, intentionally including control characters
            // eslint-disable-next-line no-control-regex
            .replace(/[^\x00-\x7F]/g, '')
    )
}

export const labOrgIds = (session: UserSession) =>
    Object.values(session.orgs)
        .filter((org) => org.type === 'lab')
        .map((org) => org.id)

export const enclaveOrgIds = (session: UserSession) =>
    Object.values(session.orgs)
        .filter((org) => org.type === 'enclave')
        .map((org) => org.id)

// Membership alone, deliberately narrower than a `view` ability: several callers gate on the
// viewer being on one specific side of the study rather than on being allowed to see it.
export const isSessionOrgMember = (session: UserSession, orgId: string): boolean =>
    Object.values(session.orgs).some((org) => org.id === orgId)

export function actionResult<T>(result: ActionResponse<T>): T {
    if (isActionError(result)) {
        throw new Error(errorToString(result))
    }
    return result
}

export function safeRedirectUrl(url: string | null | undefined, fallback: Route): Route {
    if (!url) return fallback

    if (!url.startsWith('/') || url.startsWith('//')) return fallback

    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return fallback

    let decoded: string
    try {
        decoded = decodeURIComponent(url)
    } catch {
        return fallback
    }

    // Reject double-encoded values
    try {
        if (decodeURIComponent(decoded) !== decoded) return fallback
    } catch {
        // a bare % is fine
    }

    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1F\x7F]/.test(decoded)) return fallback

    if (/\.\.|\\|\/\/|\0/.test(decoded)) return fallback

    return url as Route
}
